import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, MessageCircle, CheckCircle2, Package, MapPin } from "lucide-react";
import { api, money } from "../api";
import { useSettings } from "../context/SettingsContext";
import { buildOrderBookingBill, formatDeliveryTime } from "../utils/whatsappBill";

export default function ThankYou() {
  const { id } = useParams();
  const { settings, whatsappLink } = useSettings();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/api/orders/${id}`);
        setOrder(data);
      } catch {
        setOrder(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const orderId = order?.order_id || id;
  const waMsg = order
    ? buildOrderBookingBill({
        brandName: settings.brand_name,
        orderId,
        items: order.items,
        customer: {
          name: order.customer_name,
          phone: order.customer_phone,
          email: order.customer_email,
          address: order.address,
        },
        notes: order.notes,
        total: order.total,
        now: order.created_at,
      })
    : `Hi ${settings.brand_name}, I would like to discuss more about Order ${orderId}.`;

  return (
    <main className="thanks" data-testid="thank-you-page" style={{ maxWidth: 640, margin: "0 auto", padding: "40px 20px" }}>
      <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 48, height: 48, borderRadius: "50%", background: "var(--wine)", color: "var(--ivory)", marginBottom: 16 }}>
        <CheckCircle2 size={26} />
      </div>
      <span className="eyebrow" style={{ display: "block", marginBottom: 8 }}>Order Confirmed</span>
      <h1 style={{ marginBottom: 12 }}>
        Thank <em>you.</em>
      </h1>
      <p style={{ color: "var(--muted)", maxWidth: 500, margin: "0 auto 28px", lineHeight: 1.6 }}>
        Your order details have been securely recorded in our system. A copy has been routed to our atelier on WhatsApp for bespoke confirmation.
      </p>

      <div className="order-ticket" data-testid="thank-you-order-details" style={{ marginBottom: 24 }}>
        <div>
          <span className="eyebrow">Order ID</span>
          <strong data-testid="thank-you-order-id" style={{ color: "var(--wine)" }}>{orderId}</strong>
        </div>
        <div>
          <span className="eyebrow">Total</span>
          <strong data-testid="thank-you-total">{order ? money(order.total) : "—"}</strong>
        </div>
        <div>
          <span className="eyebrow">Studio WhatsApp</span>
          <strong>{settings.whatsapp_number}</strong>
        </div>
      </div>

      {order?.items && order.items.length > 0 && (
        <div style={{ textAlign: "left", background: "var(--paper)", border: "1px solid var(--line)", padding: 20, borderRadius: 4, marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, fontWeight: 500 }}>
            <Package size={16} /> Order Summary ({order.items.length} item{order.items.length > 1 ? "s" : ""})
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {order.items.map((item, idx) => (
              <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px dashed var(--line-soft)", paddingBottom: 8 }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{item.name}</div>
                  <div style={{ fontSize: 13, color: "var(--muted)" }}>
                    Size: {item.size} {item.color ? `| Color: ${item.color}` : ""} · Qty: {item.quantity}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3 }}>
                    Estimated delivery: {formatDeliveryTime(item.delivery_time_days)}
                  </div>
                </div>
                <div style={{ fontWeight: 500 }}>{money(item.price * item.quantity)}</div>
              </div>
            ))}
          </div>

          {order.address && (
            <div style={{ marginTop: 14, paddingTop: 10, borderTop: "1px solid var(--line)", fontSize: 13, color: "var(--muted)", display: "flex", gap: 6 }}>
              <MapPin size={15} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <strong style={{ color: "var(--ink)" }}>Deliver to: </strong>
                {order.customer_name} ({order.customer_phone}) — {order.address}
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
        <a
          href={whatsappLink(waMsg)}
          target="_blank"
          rel="noopener noreferrer"
          className="btn"
          style={{ background: "#25D366", color: "#fff", border: "none" }}
          data-testid="thank-you-whatsapp-button"
        >
          <MessageCircle size={16} /> Chat on WhatsApp
        </a>
        <Link to="/shop" className="btn btn-primary" data-testid="thank-you-continue-button">
          Continue browsing <ArrowUpRight size={16} />
        </Link>
      </div>
    </main>
  );
}
